/**
 * Textarea Component
 *
 * 여러 줄 텍스트 입력 컴포넌트
 * - rows로 초기 높이 설정
 * - maxLength로 글자 수 제한
 * - 글자 수 표시 기능
 */

export interface TextareaProps {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  rows?: number
  maxLength?: number
  error?: string
  required?: boolean
  disabled?: boolean
}

export function Textarea({
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
  maxLength,
  error,
  required = false,
  disabled = false,
}: TextareaProps) {
  const hasError = !!error

  // 베이스 스타일
  const baseClasses = 'w-full px-4 py-3 border rounded-lg text-base transition-colors resize-none'

  // 에러 상태
  const borderClasses = hasError
    ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500'
    : 'border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:border-gray-600 dark:focus:ring-primary-400'

  // 비활성화 상태
  const disabledClasses = disabled
    ? 'bg-gray-100 cursor-not-allowed text-gray-500 dark:bg-gray-900 dark:text-gray-400'
    : 'bg-white dark:bg-gray-800 dark:text-gray-100'

  const textareaClassName = [baseClasses, borderClasses, disabledClasses]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="w-full">
      {/* Label */}
      <label className="block mb-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-100">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </span>
      </label>

      {/* Textarea */}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        disabled={disabled}
        required={required}
        className={textareaClassName}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${label}-error` : undefined}
        data-error={hasError}
      />

      {/* Character Count & Error */}
      <div className="flex items-center justify-between mt-1">
        {/* Error Message */}
        {hasError ? (
          <p
            id={`${label}-error`}
            className="text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        ) : (
          <div />
        )}

        {/* Character Count */}
        {maxLength && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {value.length} / {maxLength}
          </p>
        )}
      </div>
    </div>
  )
}
